import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './ProductDetailsPage.css';

const PAGE_SIZE = 50;

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'generated', label: 'Generated' },
  { key: 'pending', label: 'Pending' },
  { key: 'failed', label: 'Failed' },
  { key: 'not_generated', label: 'Not Generated' },
];

const SHOPIFY_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pushed', label: 'Pushed' },
  { key: 'not_pushed', label: 'Not Pushed' },
  { key: 'push_failed', label: 'Push Failed' },
];

const PUSH_SCHEDULER_CONFIG = {
  'Digital Printed Fabric': {
    label: 'productDetailsPushByType · every 10 min · Monday 00:00–05:59 IST · batch up to 50',
    getWindowState() {
      const istMs = Date.now() + 5.5 * 60 * 60 * 1000;
      const ist = new Date(istMs);
      const day = ist.getUTCDay();
      const totalMin = ist.getUTCHours() * 60 + ist.getUTCMinutes();
      const isActive = day === 1 && totalMin >= 0 && totalMin < 360;
      if (isActive) return { active: true, label: 'Active now' };

      let daysUntilMonday = (1 - day + 7) % 7;
      if (day === 1 && totalMin >= 360) daysUntilMonday = 7;
      const minsUntil = daysUntilMonday * 24 * 60 + ((24 * 60) - totalMin) % (24 * 60);
      const h = Math.floor(minsUntil / 60);
      const m = minsUntil % 60;
      return { active: false, label: h > 0 ? `in ${h}h ${m}m` : `in ${m}m` };
    },
  },
};

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function StatusBadge({ status }) {
  const map = {
    generated: { label: 'Generated', cls: 'pdp-badge pdp-badge--generated' },
    pending: { label: 'Pending…', cls: 'pdp-badge pdp-badge--pending' },
    failed: { label: 'Failed', cls: 'pdp-badge pdp-badge--failed' },
    not_generated: { label: 'Not Generated', cls: 'pdp-badge pdp-badge--none' },
  };
  const entry = map[status] || map.not_generated;
  return <span className={entry.cls}>{entry.label}</span>;
}

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button className="pdp-copy-btn" onClick={handleCopy}>
      {copied ? 'Copied!' : label}
    </button>
  );
}

function HtmlBlock({ label, html }) {
  const [open, setOpen] = useState(true);
  if (!html) return null;
  return (
    <div className="pdp-json-section">
      <div className="pdp-json-section-header-row">
        <button className="pdp-json-section-header" onClick={() => setOpen((v) => !v)}>
          <span>{open ? '▼' : '▶'}</span> {label}
        </button>
        <CopyButton text={html} />
      </div>
      {open && <pre className="pdp-json-pre pdp-json-pre--html">{html}</pre>}
    </div>
  );
}

function JsonSection({ title, data }) {
  const [open, setOpen] = useState(true);
  if (!data || typeof data !== 'object') return null;
  const json = JSON.stringify(data, null, 2);
  return (
    <div className="pdp-json-section">
      <div className="pdp-json-section-header-row">
        <button className="pdp-json-section-header" onClick={() => setOpen((v) => !v)}>
          <span>{open ? '▼' : '▶'}</span> {title}
        </button>
        <CopyButton text={json} />
      </div>
      {open && <pre className="pdp-json-pre">{json}</pre>}
    </div>
  );
}

function DetailsPanel({ product, onClose, authedFetch, onPushSuccess }) {
  const [pushing, setPushing] = useState(false);
  const [pushError, setPushError] = useState('');
  // Local push status — starts from product prop, updates after push
  const [localPushStatus, setLocalPushStatus] = useState(product?.pushStatus || null);
  const [localPushedAt, setLocalPushedAt] = useState(product?.pushedToShopifyAt || null);

  // Sync if a different product is selected
  React.useEffect(() => {
    setLocalPushStatus(product?.pushStatus || null);
    setLocalPushedAt(product?.pushedToShopifyAt || null);
    setPushError('');
  }, [product?.shopifyProductId, product?.pushStatus, product?.pushedToShopifyAt]);

  if (!product) return null;
  const d = product.details || {};
  const generationError = String(product.error || '').trim();

  const handlePush = async () => {
    if (!window.confirm(`Push to Shopify?\n\nThis will update the live Shopify product:\n• Title\n• HTML Description\n• Tags\n• SEO title & description\n\nProduct: ${product.title}`)) return;
    setPushing(true);
    setPushError('');
    try {
      const res = await authedFetch(`/api/v1/product-details/${product.shopifyProductId}/push-to-shopify`, { method: 'POST' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Push failed.');
      const now = data.pushedAt || new Date().toISOString();
      setLocalPushStatus('pushed');
      setLocalPushedAt(now);
      if (onPushSuccess) onPushSuccess(product.shopifyProductId, now);
    } catch (err) {
      setPushError(err.message || 'Push failed.');
      setLocalPushStatus('failed');
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="pdp-panel">
      <div className="pdp-panel-header">
        <div className="pdp-panel-title">
          <strong>{product.title}</strong>
          <span className="pdp-panel-meta">{product.fabricName || '—'} · Generated {formatDate(product.generatedAt)}</span>
        </div>
        <div className="pdp-panel-header-actions">
          {localPushStatus === 'pushed' && (
            <span className="pdp-push-badge pdp-push-badge--pushed">✓ Pushed {formatDate(localPushedAt)}</span>
          )}
          {localPushStatus === 'failed' && (
            <span className="pdp-push-badge pdp-push-badge--failed" title={pushError}>Push Failed</span>
          )}
          <button
            className="pdp-push-btn"
            onClick={handlePush}
            disabled={pushing}
            title="Update live Shopify product with generated title, description, tags and SEO"
          >
            {pushing ? 'Pushing…' : localPushStatus === 'pushed' ? 'Re-push to Shopify' : 'Push to Shopify'}
          </button>
          <CopyButton text={JSON.stringify(d, null, 2)} label="Copy All" />
          <button className="pdp-panel-close" onClick={onClose}>✕</button>
        </div>
      </div>
      {generationError && <div className="pdp-error pdp-panel-error">{generationError}</div>}
      {pushError && <div className="pdp-error pdp-panel-error">{pushError}</div>}
      <div className="pdp-panel-body">
        <JsonSection title="Fabric Story" data={d.fabric_story} />
        {d.variant_fabric_stories && Object.keys(d.variant_fabric_stories).length > 0 && (
          <JsonSection
            title="Fabric Stories (per quality)"
            data={d.variant_fabric_stories}
          />
        )}
        {/* Fabric Set: two designs + shared visual */}
        {d.fabric_1 && <JsonSection title="Design 1" data={d.fabric_1} />}
        {d.fabric_2 && <JsonSection title="Design 2" data={d.fabric_2} />}
        {d.shared_visual && <JsonSection title="Shared Visual" data={d.shared_visual} />}
        {/* Standard single-fabric: colors + visual */}
        {!d.fabric_1 && <JsonSection title="Colors & Visual" data={{ colors: d.colors, visual: d.visual }} />}
        <JsonSection title="Usage" data={d.usage} />
        <JsonSection title="Shopify (titles / SEO / tags)" data={d.shopify ? { ...d.shopify, html_description: undefined } : null} />
        <HtmlBlock label="Shopify HTML Description" html={d.shopify?.html_description} />
        <JsonSection title="Google Shopping" data={d.google_shopping} />
        <JsonSection title="Amazon" data={d.amazon} />
        <JsonSection title="Myntra / Meesho" data={d.myntra_meesho} />
        <JsonSection title="Meta Ads" data={d.meta_ads} />
        <JsonSection title="Microsoft Search" data={d.microsoft_search} />
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  const delta = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…');
    }
  }
  return (
    <div className="pdp-pagination">
      <button className="pdp-page-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
      {pages.map((p, i) =>
        p === '…'
          ? <span key={`ellipsis-${i}`} className="pdp-page-ellipsis">…</span>
          : <button
              key={p}
              className={`pdp-page-btn${p === page ? ' pdp-page-btn--active' : ''}`}
              onClick={() => onChange(p)}
            >{p}</button>
      )}
      <button className="pdp-page-btn" disabled={page === totalPages} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}

function AutoQueuePanel({ productType, authedFetch }) {
  const [queue, setQueue] = useState(null);
  const [toggling, setToggling] = useState(false);
  const mountedRef = React.useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!productType) return;
    try {
      const res = await authedFetch(`/api/v1/product-details/auto-queue/status?productType=${encodeURIComponent(productType)}`);
      const data = await res.json();
      if (data.success && mountedRef.current) setQueue(data.queue || {});
    } catch (_) {}
  }, [authedFetch, productType]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const handleTogglePause = async () => {
    if (!productType || !queue) return;
    const nextPausedState = !queue.paused;
    setToggling(true);
    try {
      const res = await authedFetch('/api/v1/product-details/auto-queue/toggle-pause', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productType, paused: nextPausedState }),
      });
      const data = await res.json();
      if (data.success && mountedRef.current) {
        setQueue((prev) => ({ ...prev, paused: nextPausedState }));
      }
    } catch (_) {
    } finally {
      if (mountedRef.current) setToggling(false);
    }
  };

  const active = queue?.currentlyGenerating === true && !queue?.paused;
  const isPaused = queue?.paused === true;

  return (
    <div className={`pdp-queue-panel${active ? ' pdp-queue-panel--running' : ''}${isPaused ? ' pdp-queue-panel--paused' : ''}`}>
      <div className="pdp-queue-header">
        <div className="pdp-queue-title">
          <span className={`pdp-queue-dot${active ? ' pdp-queue-dot--on' : ''}${isPaused ? ' pdp-queue-dot--paused' : ''}`} />
          Auto-Generate Queue
          {isPaused && <span className="pdp-queue-paused-badge">Paused</span>}
        </div>
        <div className="pdp-queue-controls">
          <span className="pdp-queue-mode-tag">productDetailsGenerateByType · every 5 min</span>
          <button
            className={`pdp-queue-pause-btn${isPaused ? ' pdp-queue-pause-btn--paused' : ''}`}
            disabled={toggling}
            onClick={handleTogglePause}
          >
            {toggling ? 'Updating…' : isPaused ? '▶ Resume' : '⏸ Pause'}
          </button>
        </div>
      </div>
      {queue && (
        <div className="pdp-queue-stats">
          {active && queue.currentProductTitle && (
            <div className="pdp-queue-stat pdp-queue-stat--active">
              <span className="pdp-queue-stat-label">Generating</span>
              <span>{queue.currentProductTitle}</span>
            </div>
          )}
          {(queue.processedCount > 0) && (
            <div className="pdp-queue-stat">
              <span className="pdp-queue-stat-label">Processed</span>
              <span>{queue.processedCount} products</span>
            </div>
          )}
          {queue.lastProcessedTitle && (
            <div className="pdp-queue-stat">
              <span className="pdp-queue-stat-label">Last</span>
              <span>{queue.lastProcessedTitle}</span>
            </div>
          )}
          {queue.lastError && (
            <div className="pdp-queue-stat pdp-queue-stat--error">
              <span className="pdp-queue-stat-label">Error</span>
              <span>{queue.lastError}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PromptEditor({ productType, authedFetch, onPromptSaved }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [message, setMessage] = useState('');

  const loadPrompt = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await authedFetch(`/api/v1/product-details/settings?productType=${encodeURIComponent(productType)}`);
      const data = await res.json();
      if (data.success) {
        setPrompt(data.promptTemplate || '');
        setIsDefault(data.isDefault);
      } else {
        setLoadError(data.message || 'Failed to load prompt.');
      }
    } catch (err) {
      setLoadError(err.message || 'Failed to load prompt.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch, productType]);

  useEffect(() => { if (open) loadPrompt(); }, [open, loadPrompt]);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await authedFetch('/api/v1/product-details/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productType, promptTemplate: prompt }),
      });
      const data = await res.json();
      if (data.success) { setIsDefault(false); setMessage('Saved.'); if (onPromptSaved) onPromptSaved(productType); }
      else setMessage(data.message || 'Save failed.');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default prompt?')) return;
    setSaving(true);
    try {
      const res = await authedFetch('/api/v1/product-details/settings/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productType }),
      });
      const data = await res.json();
      if (data.success) { setPrompt(data.promptTemplate); setIsDefault(true); setMessage('Reset to default.'); }
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  return (
    <div className="pdp-prompt-editor">
      <button className="pdp-prompt-toggle" onClick={() => setOpen((v) => !v)}>
        <span>{open ? '▼' : '▶'}</span>
        Prompt Template
        {!isDefault && <span className="pdp-prompt-badge">Customised</span>}
      </button>
      {open && (
        <div className="pdp-prompt-body">
          {loading ? (
            <div className="pdp-prompt-loading">Loading…</div>
          ) : loadError ? (
            <div className="pdp-error" style={{marginBottom: 0}}>{loadError} <button className="pdp-refresh-btn" style={{marginLeft:8}} onClick={loadPrompt}>Retry</button></div>
          ) : (
            <>
              <textarea
                className="pdp-prompt-textarea"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                spellCheck={false}
              />
              <div className="pdp-prompt-footer">
                <div className="pdp-prompt-actions">
                  <button className="pdp-generate-btn" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Prompt'}</button>
                  <button className="pdp-refresh-btn" onClick={handleReset} disabled={saving || isDefault}>Reset to Default</button>
                </div>
                {message && <span className="pdp-prompt-msg">{message}</span>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PushQueuePanel({ authedFetch, productType }) {
  const [queue, setQueue] = useState(null);
  const mountedRef = React.useRef(true);
  const schedulerConfig = PUSH_SCHEDULER_CONFIG[productType] || null;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!productType) return;
    try {
      const res = await authedFetch(`/api/v1/product-details/push-queue/status?productType=${encodeURIComponent(productType)}`);
      const data = await res.json();
      if (data.success && mountedRef.current) setQueue(data.queue || {});
    } catch (_) {}
  }, [authedFetch, productType]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const windowState = useMemo(() => (
    schedulerConfig ? schedulerConfig.getWindowState() : { active: false, label: 'Manual only' }
  ), [schedulerConfig]);

  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceUpdate((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const active = windowState.active && queue?.currentlyPushing;

  return (
    <div className={`pdp-queue-panel${active ? ' pdp-queue-panel--running' : ''}`}>
      <div className="pdp-queue-header">
        <div className="pdp-queue-title">
          <span className={`pdp-queue-dot${active ? ' pdp-queue-dot--on' : ''}`} />
          Shopify Push Queue
          <span className="pdp-queue-countdown">{windowState.label}</span>
        </div>
        <div className="pdp-queue-controls">
          <span className="pdp-queue-mode-tag">{schedulerConfig ? schedulerConfig.label : 'No scheduler · manual push only'}</span>
        </div>
      </div>
      {queue && (
        <div className="pdp-queue-stats">
          {queue.currentlyPushing && queue.currentProductTitle && (
            <div className="pdp-queue-stat pdp-queue-stat--active">
              <span className="pdp-queue-stat-label">Pushing</span>
              <span>{queue.currentProductTitle}</span>
            </div>
          )}
          <div className="pdp-queue-stat">
            <span className="pdp-queue-stat-label">Pushed</span>
            <span>{queue.processedCount ?? 0} products</span>
          </div>
          {queue.lastPushedTitle && (
            <div className="pdp-queue-stat">
              <span className="pdp-queue-stat-label">Last</span>
              <span>{queue.lastPushedTitle} · {formatDate(queue.lastPushedAt)}</span>
            </div>
          )}
          {queue.lastError && (
            <div className="pdp-queue-stat pdp-queue-stat--error">
              <span className="pdp-queue-stat-label">Error</span>
              <span>{queue.lastError}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProductDetailsPage() {
  const { currentUser } = useAuth();
  const [productTypes, setProductTypes] = useState([]);
  const [typeMap, setTypeMap] = useState({}); // type → { hasPrompt, isDefault }
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [productType, setProductType] = useState('Digital Printed Fabric');
  const [generating, setGenerating] = useState(new Set());
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [shopifyFilter, setShopifyFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState({ all: 0, generated: 0, not_generated: 0, failed: 0, pending: 0 });
  const [shopifyCounts, setShopifyCounts] = useState({ all: 0, pushed: 0, not_pushed: 0, push_failed: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = no search; [] = searched, none found
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [repairing, setRepairing] = useState(false);
  const [repairMessage, setRepairMessage] = useState('');
  const [batchPushing, setBatchPushing] = useState(false);
  const [batchPushMessage, setBatchPushMessage] = useState('');
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchGenerateMessage, setBatchGenerateMessage] = useState('');

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('Not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = base && url.startsWith('/') ? `${base}${url}` : url;
    return fetch(resolvedUrl, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) },
    });
  }, [currentUser]);

  const loadTypes = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v1/product-details/types');
      const data = await res.json();
      if (data.success) {
        setProductTypes(data.types.map((t) => t.type));
        const map = {};
        data.types.forEach((t) => { map[t.type] = { hasPrompt: t.hasPrompt, isDefault: t.isDefault }; });
        setTypeMap(map);
      }
    } catch (_) {}
  }, [authedFetch]);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  const handlePromptSaved = useCallback((type) => {
    setTypeMap((prev) => ({ ...prev, [type]: { ...(prev[type] || {}), hasPrompt: true } }));
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        productType,
        page: String(page),
        limit: '10',
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(shopifyFilter && shopifyFilter !== 'all' ? { shopifyFilter } : {}),
      });
      const res = await authedFetch(`/api/v1/product-details/products?${params}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load products.');
      setProducts(data.products || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
      if (data.counts) setCounts(data.counts);
      if (data.shopifyCounts) setShopifyCounts(data.shopifyCounts);
    } catch (err) {
      setError(err.message || 'Failed to load products.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch, productType, page, shopifyFilter, statusFilter]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handlePushSuccess = useCallback((productId, pushedAt) => {
    setProducts((prev) => prev.map((p) =>
      p.shopifyProductId === productId
        ? { ...p, pushStatus: 'pushed', pushedToShopifyAt: pushedAt }
        : p
    ));
    setSelectedProduct((prev) => prev?.shopifyProductId === productId
      ? { ...prev, pushStatus: 'pushed', pushedToShopifyAt: pushedAt }
      : prev
    );
  }, []);

  const handleTickComplete = useCallback(({ productId, details }) => {
    const now = new Date().toISOString();
    setProducts((prev) => prev.map((p) =>
      p.shopifyProductId === productId
        ? { ...p, generationStatus: 'generated', generatedAt: now, details }
        : p
    ));
  }, []);

  const handleTypeSelect = useCallback((type) => {
    setProductType(type);
    setProducts([]);
    setStatusFilter(null);
    setShopifyFilter('all');
    setPage(1);
    setSelectedProduct(null);
  }, []);

  // Reset to page 1 when filter changes
  useEffect(() => { setPage(1); }, [statusFilter, shopifyFilter]);

  const handleGenerate = useCallback(async (product) => {
    const id = product.shopifyProductId;
    setGenerating((prev) => new Set([...prev, id]));
    setProducts((prev) => prev.map((p) => p.shopifyProductId === id ? { ...p, generationStatus: 'pending' } : p));
    try {
      const res = await authedFetch(`/api/v1/product-details/${id}/generate`, { method: 'POST' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Generation failed.');
      const now = new Date().toISOString();
      setProducts((prev) => prev.map((p) =>
        p.shopifyProductId === id
          ? { ...p, generationStatus: 'generated', generatedAt: now, details: data.details }
          : p
      ));
      if (selectedProduct?.shopifyProductId === id) {
        setSelectedProduct((prev) => prev ? { ...prev, generationStatus: 'generated', generatedAt: now, details: data.details } : prev);
      }
    } catch (_) {
      setProducts((prev) => prev.map((p) => p.shopifyProductId === id ? { ...p, generationStatus: 'failed' } : p));
    } finally {
      setGenerating((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  }, [authedFetch, selectedProduct]);

  const handleRowClick = useCallback(async (product) => {
    if (product.generationStatus !== 'generated' && product.generationStatus !== 'failed') return;
    if (product.details) { setSelectedProduct(product); return; }
    try {
      const res = await authedFetch(`/api/v1/product-details/${product.shopifyProductId}`);
      const data = await res.json();
      if (data.success) {
        setSelectedProduct({
          ...product,
          details: data.detail?.details,
          error: data.detail?.error || product.error || '',
        });
      }
    } catch (_) {
      setSelectedProduct(product);
    }
  }, [authedFetch]);

  const handleSearch = useCallback(async (e) => {
    if (e) e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setSearching(true);
    setSearchError('');
    setSearchResults(null);
    try {
      const res = await authedFetch(`/api/v1/product-details/search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Search failed.');
      setSearchResults(data.products || []);
    } catch (err) {
      setSearchError(err.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  }, [authedFetch, searchQuery]);

  const handleSearchClear = useCallback(() => {
    setSearchQuery('');
    setSearchResults(null);
    setSearchError('');
  }, []);

  const handleRepair = useCallback(async () => {
    if (!window.confirm('Reset all generated products missing fabric stories to pending so they get regenerated. Continue?')) return;
    setRepairing(true);
    setRepairMessage('');
    try {
      const res = await authedFetch('/api/v1/product-details/repair-fabric-stories', { method: 'POST' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Repair failed.');
      setRepairMessage(data.count === 0 ? 'All products already have fabric stories.' : `${data.count} product(s) queued for regeneration.`);
      if (data.count > 0) loadProducts();
    } catch (err) {
      setRepairMessage(err.message || 'Repair failed.');
    } finally {
      setRepairing(false);
    }
  }, [authedFetch, loadProducts]);

  const handleBatchPush = useCallback(async () => {
    if (!productType) return;
    if (!window.confirm(`Push up to 20 generated, not-yet-pushed "${productType}" products to Shopify?`)) return;
    setBatchPushing(true);
    setBatchPushMessage('');
    try {
      const res = await authedFetch('/api/v1/product-details/push-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productType, limit: 20 }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Batch push failed.');
      const summary = data.summary || {};
      setBatchPushMessage(summary.message || `Pushed ${summary.pushed || 0} product(s).`);
      loadProducts();
    } catch (err) {
      setBatchPushMessage(err.message || 'Batch push failed.');
    } finally {
      setBatchPushing(false);
    }
  }, [authedFetch, loadProducts, productType]);

  const handleBatchGenerate = useCallback(async () => {
    if (!productType) return;
    if (!window.confirm(`Generate up to 20 pending "${productType}" products?`)) return;
    setBatchGenerating(true);
    setBatchGenerateMessage('');
    try {
      const res = await authedFetch('/api/v1/product-details/generate-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productType, limit: 20 }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Batch generation failed.');
      const summary = data.summary || {};
      setBatchGenerateMessage(summary.message || `Generated ${summary.succeeded || 0} product(s).`);
      loadProducts();
    } catch (err) {
      setBatchGenerateMessage(err.message || 'Batch generation failed.');
    } finally {
      setBatchGenerating(false);
    }
  }, [authedFetch, loadProducts, productType]);

  return (
    <div className={`pdp-layout${selectedProduct ? ' pdp-layout--split' : ''}`}>
      <div className="pdp-main">
        <div className="pdp-header">
          <h1 className="pdp-title">Product Details AI</h1>
          <div className="pdp-controls">
            <select
              className="pdp-type-select"
              value={productType}
              onChange={(e) => handleTypeSelect(e.target.value)}
            >
              {(productTypes.length ? productTypes : [productType]).map((t) => {
                const info = typeMap[t];
                return (
                  <option key={t} value={t}>{t}{info && !info.hasPrompt ? ' (no prompt)' : ''}</option>
                );
              })}
            </select>
            <button
              className="pdp-batch-generate-btn"
              onClick={handleBatchGenerate}
              disabled={batchGenerating}
              title={`Generate up to 20 pending ${productType} products`}
            >
              {batchGenerating ? 'Generating 20…' : 'Generate 20 Now'}
            </button>
            <button
              className="pdp-batch-push-btn"
              onClick={handleBatchPush}
              disabled={batchPushing}
              title={`Push up to 20 generated ${productType} products that are not yet pushed`}
            >
              {batchPushing ? 'Pushing 20…' : 'Push 20 Now'}
            </button>
            <form className="pdp-search-form" onSubmit={handleSearch}>
              <input
                className="pdp-search-input"
                type="text"
                placeholder="Search by SKU, product ID or variant ID…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button className="pdp-search-btn" type="submit" disabled={searching || !searchQuery.trim()}>
                {searching ? '…' : '🔍'}
              </button>
              {searchResults !== null && (
                <button className="pdp-search-clear-btn" type="button" onClick={handleSearchClear} title="Clear search">✕</button>
              )}
            </form>
            <button className="pdp-refresh-btn" onClick={loadProducts} disabled={loading}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
            <button className="pdp-repair-btn" onClick={handleRepair} disabled={repairing} title="Reset products missing fabric stories so they get regenerated">
              {repairing ? 'Repairing…' : '🔧 Repair Stories'}
            </button>
          </div>
        </div>
        {repairMessage && (
          <div className={`pdp-repair-msg${repairMessage.includes('queued') ? ' pdp-repair-msg--ok' : ''}`}>
            {repairMessage}
            <button className="pdp-repair-msg-close" onClick={() => setRepairMessage('')}>✕</button>
          </div>
        )}
        {batchPushMessage && (
          <div className={`pdp-repair-msg${!batchPushMessage.toLowerCase().includes('failed') ? ' pdp-repair-msg--ok' : ''}`}>
            {batchPushMessage}
            <button className="pdp-repair-msg-close" onClick={() => setBatchPushMessage('')}>✕</button>
          </div>
        )}
        {batchGenerateMessage && (
          <div className={`pdp-repair-msg${!batchGenerateMessage.toLowerCase().includes('failed') ? ' pdp-repair-msg--ok' : ''}`}>
            {batchGenerateMessage}
            <button className="pdp-repair-msg-close" onClick={() => setBatchGenerateMessage('')}>✕</button>
          </div>
        )}

        {searchResults !== null && (
          <div className="pdp-search-results">
            {searchError && <div className="pdp-error">{searchError}</div>}
            {!searchError && searchResults.length === 0 && (
              <div className="pdp-search-empty">No products found for "{searchQuery}".</div>
            )}
            {!searchError && searchResults.length > 0 && (
              <>
                <div className="pdp-search-count">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"</div>
                <div className="pdp-table-wrap">
                  <table className="pdp-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Product</th>
                        <th>Fabric</th>
                        <th>Quality</th>
                        <th>Status</th>
                        <th>Generated</th>
                        <th>Shopify</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((product) => {
                        const isGenerating = generating.has(product.shopifyProductId);
                        const isSelected = selectedProduct?.shopifyProductId === product.shopifyProductId;
                        return (
                          <tr
                            key={product.shopifyProductId}
                            className={`pdp-row${isSelected ? ' pdp-row--selected' : ''}${product.generationStatus === 'generated' ? ' pdp-row--clickable' : ''}`}
                            onClick={() => handleRowClick(product)}
                          >
                            <td className="pdp-thumb-cell">
                              {product.featuredImageUrl
                                ? <img src={product.featuredImageUrl} alt="" className="pdp-thumb" />
                                : <div className="pdp-thumb pdp-thumb--empty" />}
                            </td>
                            <td className="pdp-title-cell">
                              <span className="pdp-product-title">{product.title}</span>
                              {!product.hasFabricSpec && <span className="pdp-warn">No fabric spec</span>}
                              {product.generationStatus === 'failed' && product.error && (
                                <span className="pdp-row-error" title={product.error}>{product.error}</span>
                              )}
                            </td>
                            <td>{product.fabricName || '—'}</td>
                            <td>{product.fabricQuality || '—'}</td>
                            <td><StatusBadge status={product.generationStatus} /></td>
                            <td className="pdp-date">{formatDate(product.generatedAt)}</td>
                            <td className="pdp-push-cell">
                              {product.pushStatus === 'pushed' && (
                                <span className="pdp-push-badge pdp-push-badge--pushed" title={`Pushed ${formatDate(product.pushedToShopifyAt)}`}>✓ Pushed</span>
                              )}
                              {product.pushStatus === 'failed' && (
                                <span className="pdp-push-badge pdp-push-badge--failed">Push Failed</span>
                              )}
                            </td>
                            <td className="pdp-action-cell" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="pdp-generate-btn"
                                disabled={isGenerating || !product.hasFabricSpec || typeMap[productType]?.hasPrompt === false}
                                onClick={() => handleGenerate(product)}
                                title={!product.hasFabricSpec ? 'No fabric spec matched' : ''}
                              >
                                {isGenerating ? 'Generating…' : product.generationStatus === 'generated' ? 'Regenerate' : 'Generate'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        <AutoQueuePanel
          productType={productType}
          authedFetch={authedFetch}
        />
        <PromptEditor
          productType={productType}
          authedFetch={authedFetch}
          onPromptSaved={handlePromptSaved}
        />
        <PushQueuePanel authedFetch={authedFetch} productType={productType} />

        <div className="pdp-filters">
          <div className="pdp-filter-group">
            <span className="pdp-filter-label">Generation</span>
            <div className="pdp-filter-pills">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.key}
                  className={`pdp-pill${(statusFilter || 'all') === f.key ? ' pdp-pill--active' : ''}`}
                  onClick={() => setStatusFilter(f.key === 'all' ? null : f.key)}
                >
                  {f.label}
                  <span className="pdp-pill-count">{counts[f.key] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="pdp-filter-group">
            <span className="pdp-filter-label">Shopify</span>
            <div className="pdp-filter-pills">
              {SHOPIFY_FILTERS.map((f) => (
                <button
                  key={f.key}
                  className={`pdp-pill${shopifyFilter === f.key ? ' pdp-pill--active' : ''}`}
                  onClick={() => setShopifyFilter(f.key)}
                >
                  {f.label}
                  <span className="pdp-pill-count">{shopifyCounts[f.key] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <div className="pdp-error">{error}</div>}

        {loading ? (
          <div className="pdp-loading">Loading products…</div>
        ) : (
          <>
            <div className="pdp-table-meta">
              Showing {totalCount === 0 ? 0 : (page - 1) * 10 + 1}–{Math.min(page * 10, totalCount)} of {totalCount} products
            </div>
            <div className="pdp-table-wrap">
              <table className="pdp-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Product</th>
                    <th>Fabric</th>
                    <th>Quality</th>
                    <th>Status</th>
                    <th>Generated</th>
                    <th>Shopify</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 && (
                    <tr><td colSpan={8} className="pdp-empty">No products match this filter.</td></tr>
                  )}
                  {products.map((product) => {
                    const isGenerating = generating.has(product.shopifyProductId);
                    const isSelected = selectedProduct?.shopifyProductId === product.shopifyProductId;
                    return (
                      <tr
                        key={product.shopifyProductId}
                        className={`pdp-row${isSelected ? ' pdp-row--selected' : ''}${product.generationStatus === 'generated' ? ' pdp-row--clickable' : ''}`}
                        onClick={() => handleRowClick(product)}
                      >
                        <td className="pdp-thumb-cell">
                          {product.featuredImageUrl
                            ? <img src={product.featuredImageUrl} alt="" className="pdp-thumb" />
                            : <div className="pdp-thumb pdp-thumb--empty" />}
                        </td>
                        <td className="pdp-title-cell">
                          <span className="pdp-product-title">{product.title}</span>
                          {!product.hasFabricSpec && (
                            <span className="pdp-warn">No fabric spec</span>
                          )}
                          {product.generationStatus === 'failed' && product.error && (
                            <span className="pdp-row-error" title={product.error}>{product.error}</span>
                          )}
                        </td>
                        <td>{product.fabricName || '—'}</td>
                        <td>{product.fabricQuality || '—'}</td>
                        <td><StatusBadge status={product.generationStatus} /></td>
                        <td className="pdp-date">{formatDate(product.generatedAt)}</td>
                        <td className="pdp-push-cell">
                          {product.pushStatus === 'pushed' && (
                            <span className="pdp-push-badge pdp-push-badge--pushed" title={`Pushed ${formatDate(product.pushedToShopifyAt)}`}>✓ Pushed</span>
                          )}
                          {product.pushStatus === 'failed' && (
                            <span className="pdp-push-badge pdp-push-badge--failed">Push Failed</span>
                          )}
                        </td>
                        <td className="pdp-action-cell" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="pdp-generate-btn"
                            disabled={isGenerating || !product.hasFabricSpec || typeMap[productType]?.hasPrompt === false}
                            onClick={() => handleGenerate(product)}
                            title={!product.hasFabricSpec ? 'No fabric spec matched for this product' : typeMap[productType]?.hasPrompt === false ? 'Add a prompt template first' : ''}
                          >
                            {isGenerating ? 'Generating…' : product.generationStatus === 'generated' ? 'Regenerate' : 'Generate'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>

      {selectedProduct && (
        <DetailsPanel
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          authedFetch={authedFetch}
          onPushSuccess={handlePushSuccess}
        />
      )}
    </div>
  );
}
